import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { createOrgStaff } from '../../../../modules/accessControl/actions';
import { selectOrgRoles, selectOrgRolesLoading, selectCreateOrgStaffLoading } from '../../../../modules/accessControl/selectors';

interface StaffModalProps {
  open: boolean;
  onClose: () => void;
}

const StaffModal: React.FC<StaffModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const roles = useSelector(selectOrgRoles);
  const rolesLoading = useSelector(selectOrgRolesLoading);
  const isSubmitting = useSelector(selectCreateOrgStaffLoading);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    role_id: ''
  });

  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (open) {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        role_id: ''
      });
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.first_name) newErrors.first_name = 'Required';
    if (!formData.last_name) newErrors.last_name = 'Required';
    if (!formData.email) {
      newErrors.email = 'Required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }
    if (!formData.role_id) newErrors.role_id = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      dispatch(createOrgStaff(formData));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Staff Member</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First Name"
              fullWidth
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              error={!!errors.first_name}
              helperText={errors.first_name}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              error={!!errors.last_name}
              helperText={errors.last_name}
            />
          </Box>
          <TextField
            label="Email"
            fullWidth
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={!!errors.email}
            helperText={errors.email}
          />
          <TextField
            label="Phone Number"
            fullWidth
            value={formData.phone_number}
            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
            error={!!errors.phone_number}
            helperText={errors.phone_number}
          />
          <TextField
            select
            label="System Role"
            fullWidth
            value={formData.role_id}
            onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
            error={!!errors.role_id}
            helperText={errors.role_id}
            disabled={rolesLoading}
          >
            {rolesLoading ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} /> Loading roles...
              </MenuItem>
            ) : (
              roles.map((role) => (
                <MenuItem key={role.role_id} value={role.role_id}>
                  {role.role_name}
                </MenuItem>
              ))
            )}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            backgroundColor: '#5E8E7E',
            '&:hover': { backgroundColor: '#4A7A6A' }
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Add Staff'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffModal;
